import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Input, Spinner, Text } from '@chakra-ui/react'
import { TicketService } from '@/lib/services/ticket'
import type { ToolPartCatalogOption } from '@/types/ticket'
import { useThemeColors } from '@/hooks/useThemeColors'

type Props = {
  value: string
  catalogId?: number
  onChange: (name: string, catalogId?: number) => void
  placeholder?: string
  inputBaseProps?: Record<string, unknown>
}

export function ToolPartNameInput({
  value,
  onChange,
  placeholder = 'Tool / part name',
  inputBaseProps,
}: Props) {
  const colors = useThemeColors()
  const [searching, setSearching] = useState(false)
  const [matches, setMatches] = useState<ToolPartCatalogOption[]>([])
  const [suggestions, setSuggestions] = useState<ToolPartCatalogOption[]>([])
  const [canCreate, setCanCreate] = useState(false)
  const [focused, setFocused] = useState(false)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const latestQueryRef = useRef('')

  const runSearch = useCallback(async (term: string) => {
    if (term.trim().length < 2) {
      setMatches([])
      setSuggestions([])
      setCanCreate(false)
      return
    }

    setSearching(true)
    latestQueryRef.current = term
    try {
      const response = await TicketService.searchToolPartOptions(term)
      if (!response.success || !response.data) {
        setMatches([])
        setSuggestions([])
        setCanCreate(false)
        return
      }
      if (latestQueryRef.current !== term) return
      setMatches(response.data.matches)
      setSuggestions(response.data.suggestions)
      setCanCreate(response.data.canCreate)
    } finally {
      if (latestQueryRef.current === term) {
        setSearching(false)
      }
    }
  }, [])

  const handleInputChange = (next: string) => {
    onChange(next, undefined)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      void runSearch(next)
    }, 300)
  }

  const selectOption = (option: ToolPartCatalogOption) => {
    onChange(option.name, option.id)
    setMatches([])
    setSuggestions([])
    setCanCreate(false)
    setFocused(false)
  }

  const createOption = async () => {
    const name = value.trim()
    if (!name) return
    const response = await TicketService.upsertToolPart(name)
    if (response.success && response.data) {
      onChange(response.data.name, response.data.id)
    }
    setMatches([])
    setSuggestions([])
    setCanCreate(false)
  }

  const shouldShowDropdown = useMemo(
    () => focused && (searching || matches.length > 0 || suggestions.length > 0 || canCreate),
    [focused, searching, matches.length, suggestions.length, canCreate],
  )

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  return (
    <Box position="relative">
      <Input
        size="sm"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => {
          setFocused(true)
          void runSearch(value)
        }}
        onBlur={() => {
          setTimeout(() => setFocused(false), 120)
        }}
        onChange={(e) => handleInputChange(e.target.value)}
        {...inputBaseProps}
      />
      {searching ? (
        <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
          <Spinner size="sm" color={colors.primary} />
        </Box>
      ) : null}

      {shouldShowDropdown ? (
        <Box
          position="absolute"
          zIndex={10}
          w="100%"
          mt={1}
          bg="white"
          border="1px solid"
          borderColor={colors.borderLight}
          borderRadius="md"
          boxShadow="md"
          maxH="240px"
          overflowY="auto"
        >
          {matches.map((option) => (
            <Box
              key={`match-${option.id}`}
              px={4}
              py={2}
              cursor="pointer"
              _hover={{ bg: 'gray.100' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectOption(option)}
              borderBottom="1px solid"
              borderColor={colors.borderLight}
            >
              <Text fontWeight="600" fontSize="sm" color={colors.text}>
                {option.name}
              </Text>
            </Box>
          ))}

          {suggestions.length > 0 ? (
            <Box px={4} py={1} borderTop="1px solid" borderColor={colors.borderLight} bg="gray.50">
              <Text fontSize="xs" color={colors.textSecondary}>Did you mean?</Text>
            </Box>
          ) : null}
          {suggestions.map((option) => (
            <Box
              key={`suggest-${option.id}`}
              px={4}
              py={2}
              cursor="pointer"
              _hover={{ bg: 'gray.100' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectOption(option)}
              borderBottom="1px solid"
              borderColor={colors.borderLight}
            >
              <Text fontSize="sm" color={colors.text}>
                {option.name}
              </Text>
            </Box>
          ))}

          {canCreate && value.trim() ? (
            <Box
              px={4}
              py={2}
              cursor="pointer"
              _hover={{ bg: 'gray.100' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => void createOption()}
            >
              <Text fontSize="sm" color={colors.text}>
                Add "{value.trim()}"
              </Text>
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  )
}
